import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, FormControlLabel, InputAdornment, MenuItem, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { signageService } from '../../services/signageService';
import {
  ISignageItem,
  ISignageItemRequest,
  MAX_UPLOAD_BYTES,
  SignageMediaType,
  SignageMode,
  SignageOrientation,
  SignageSource,
  ACCEPTED_UPLOAD_TYPES,
  SIGNAGE_STATES,
  SignageState,
} from '../../types/signage';
import ConfirmDialog from './ConfirmDialog';
import MediaThumb from './MediaThumb';
import { mediaDownloadName, saveBlob } from './downloadMedia';
import { formatBytes, IMAGE_DEFAULT_SECONDS } from './playback';

export const guessMediaTypeFromUrl = (url: string): SignageMediaType | undefined => {
  let path = url;
  try {
    path = new URL(url).pathname;
  } catch {
    // not a URL yet; fall through with the raw text
  }
  const extension = path.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1];
  if (extension === 'mp4' || extension === 'webm') return 'video';
  if (extension === 'gif') return 'gif';
  if (extension && ['jpg', 'jpeg', 'png', 'webp'].includes(extension)) return 'image';
  return undefined;
};

export const isHttpsUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === 'https:' && !!parsed.host;
  } catch {
    return false;
  }
};

interface ItemEditorProps {
  storeId: string;
  // Mode (entrance or exit device) and state a new item is created in; an existing item keeps its own.
  mode?: SignageMode;
  state?: SignageState;
  item: ISignageItem | null; // null = create
  canManage: boolean;
  // How the store's device is held: the scale applies to the height in portrait and the width in landscape.
  orientation?: SignageOrientation;
  // Reports the scale being typed (undefined while it is not valid) so the preview can follow it before saving.
  onScaleChange?: (scale: number | undefined) => void;
  onSaved: (item: ISignageItem) => void;
  onCancel: () => void;
  onDeleted: (id: string) => void;
}

/** The scale as a number, or undefined when it is not a whole number of at least 1. */
export const parseScale = (text: string): number | undefined => {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;
  const value = Number(trimmed);
  return value >= 1 ? value : undefined;
};

const SCALE_PRESETS = [50, 75, 100, 125, 150];

const ItemEditor: React.FC<ItemEditorProps> = ({ storeId, mode = 'entrance', state = 'waiting', item, canManage, orientation, onScaleChange, onSaved, onCancel, onDeleted }) => {
  const isEdit = !!item;
  const [itemState, setItemState] = useState<SignageState>(item?.state ?? state);
  const [source, setSource] = useState<SignageSource>(item?.source ?? 'url');
  const [url, setUrl] = useState(item?.sourceUrl ?? '');
  const [fileId, setFileId] = useState(item?.fileId ?? '');
  const [fileName, setFileName] = useState(item?.fileName ?? '');
  const [fileSize, setFileSize] = useState(item?.fileSizeBytes ?? 0);
  const [mediaType, setMediaType] = useState<SignageMediaType>(item?.mediaType ?? 'image');
  const [eyebrow, setEyebrow] = useState(item?.eyebrow ?? '');
  const [title, setTitle] = useState(item?.title ?? '');
  const [subtitle, setSubtitle] = useState(item?.subtitle ?? '');
  const [tag, setTag] = useState(item?.tag ?? '');
  const [duration, setDuration] = useState(String(item?.durationSeconds ?? IMAGE_DEFAULT_SECONDS));
  const [loops, setLoops] = useState(String(item?.loopCount ?? 1));
  const [scale, setScale] = useState(String(item?.scalePercent ?? 100));
  const [enabled, setEnabled] = useState(item?.enabled ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Let the preview follow the scale as it is typed.
  useEffect(() => {
    onScaleChange?.(parseScale(scale));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale]);

  const changeUrl = (value: string) => {
    setUrl(value);
    const guessed = guessMediaTypeFromUrl(value);
    if (guessed) setMediaType(guessed);
  };

  const handleFile = async (file?: File) => {
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setErrors((previous) => ({ ...previous, file: 'File is larger than 25 MB.' }));
      return;
    }
    setUploading(true);
    try {
      const uploaded = await signageService.uploadFile(file);
      setFileId(uploaded.id);
      setFileName(uploaded.filename);
      setFileSize(uploaded.sizeBytes);
      setMediaType(uploaded.mediaType);
      setErrors((previous) => {
        const { file: _removed, ...rest } = previous;
        return rest;
      });
    } catch (error: any) {
      setErrors((previous) => ({ ...previous, file: typeof error === 'string' ? error : 'Upload failed' }));
    } finally {
      setUploading(false);
    }
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (source === 'url') {
      if (!isHttpsUrl(url)) next.url = 'Enter a valid https:// link.';
      else if (!guessMediaTypeFromUrl(url) && !mediaType) next.url = 'Choose the media type for this link.';
    } else if (!fileId) {
      next.file = 'Choose a file to upload.';
    }
    if (mediaType === 'image') {
      const seconds = Number(duration);
      if (!Number.isInteger(seconds) || seconds < 1 || seconds > 120) next.duration = 'Enter 1-120 seconds.';
    } else {
      const count = Number(loops);
      if (!Number.isInteger(count) || count < 1 || count > 20) next.loops = 'Enter 1-20 loops.';
    }
    if (parseScale(scale) === undefined) next.scale = 'Enter a whole number of at least 1.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const request: ISignageItemRequest = {
      storeId,
      mode: item?.mode ?? mode,
      state: item?.state ?? itemState,
      source,
      sourceUrl: source === 'url' ? url.trim() : undefined,
      fileId: source === 'file' ? fileId : undefined,
      mediaType,
      eyebrow: eyebrow.trim(),
      title: title.trim(),
      subtitle: subtitle.trim(),
      tag: tag.trim(),
      durationSeconds: mediaType === 'image' ? Number(duration) : undefined,
      loopCount: mediaType === 'image' ? undefined : Number(loops),
      scalePercent: parseScale(scale),
      enabled,
    };
    setSaving(true);
    try {
      const saved = isEdit ? await signageService.updateItem(item!.id, request) : await signageService.createItem(request);
      onSaved(saved);
    } catch (error: any) {
      setErrors({ form: typeof error === 'string' ? error : 'Failed to save item' });
    } finally {
      setSaving(false);
    }
  };

  // An uploaded file is fetched with the login and saved; a link lives on another site, so it opens there.
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await signageService.getFileBlob(fileId);
      saveBlob(blob, mediaDownloadName(fileName || undefined, title || item?.title || '', blob.type, mediaType));
    } catch (error: any) {
      setErrors((previous) => ({ ...previous, form: typeof error === 'string' ? error : 'Failed to download the file' }));
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    try {
      await signageService.deleteItem(item!.id);
      onDeleted(item!.id);
    } catch (error: any) {
      setErrors({ form: typeof error === 'string' ? error : 'Failed to delete item' });
    }
  };

  const previewFileId = source === 'file' ? fileId || undefined : undefined;
  const previewUrl = source === 'url' && isHttpsUrl(url) ? url.trim() : undefined;

  return (
    <Stack spacing={2} data-testid="signage-item-editor">
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {isEdit ? 'Edit media' : 'Add media'}
      </Typography>
      {errors.form && <Alert severity="error">{errors.form}</Alert>}
      <TextField
        select
        label="Show when"
        value={itemState}
        onChange={(e) => setItemState(e.target.value as SignageState)}
        disabled={!canManage || isEdit}
        helperText={isEdit ? 'Cannot be changed. Delete and add the media again to move it.' : SIGNAGE_STATES.find((option) => option.value === itemState)?.hint}
        fullWidth
      >
        {SIGNAGE_STATES.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <ToggleButtonGroup exclusive fullWidth size="small" value={source} onChange={(_, value: SignageSource | null) => value && setSource(value)} disabled={!canManage}>
        <ToggleButton value="url">External link</ToggleButton>
        <ToggleButton value="file">Upload file</ToggleButton>
      </ToggleButtonGroup>

      {source === 'url' ? (
        <TextField label="Media URL (https only)" value={url} onChange={(e) => changeUrl(e.target.value)} error={!!errors.url} helperText={errors.url} placeholder="https://cdn.example.com/promo.mp4" fullWidth disabled={!canManage} />
      ) : (
        <Box>
          <Button component="label" variant="outlined" fullWidth disabled={!canManage || uploading} startIcon={uploading ? <CircularProgress size={16} /> : undefined}>
            {uploading ? 'Uploading...' : fileId ? `${fileName} (${formatBytes(fileSize)}) - replace` : 'Choose file'}
            <input
              type="file"
              hidden
              accept={ACCEPTED_UPLOAD_TYPES}
              data-testid="signage-file-input"
              onChange={(e) => {
                handleFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </Button>
          <Typography variant="caption" color={errors.file ? 'error' : 'text.secondary'}>
            {errors.file || 'JPEG, PNG, WebP, GIF, MP4 or WebM. Max 25 MB. Stored on the coupons server.'}
          </Typography>
        </Box>
      )}

      {(previewFileId || previewUrl) && <MediaThumb mediaType={mediaType} fileId={previewFileId} sourceUrl={previewUrl} width="100%" height={140} />}
      {previewFileId && (
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload} disabled={downloading} sx={{ alignSelf: 'flex-start' }}>
          {downloading ? 'Downloading...' : `Download ${mediaType === 'video' ? 'video' : mediaType === 'gif' ? 'GIF' : 'image'}`}
        </Button>
      )}
      {previewUrl && (
        <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} component="a" href={previewUrl} target="_blank" rel="noopener noreferrer" sx={{ alignSelf: 'flex-start' }}>
          Open the link to download
        </Button>
      )}

      <TextField select label="Media type" value={mediaType} onChange={(e) => setMediaType(e.target.value as SignageMediaType)} disabled={!canManage || source === 'file'} helperText={source === 'file' ? 'Detected from the uploaded file' : undefined} fullWidth>
        <MenuItem value="image">Image</MenuItem>
        <MenuItem value="gif">GIF (animated)</MenuItem>
        <MenuItem value="video">Video</MenuItem>
      </TextField>

      <Box>
        <TextField
          label="Media scale"
          type="number"
          value={scale}
          onChange={(e) => setScale(e.target.value)}
          error={!!errors.scale}
          disabled={!canManage}
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          inputProps={{ min: 1, step: 1, 'aria-label': 'Media scale' }}
          fullWidth
        />
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          {errors.scale ||
            `Of the ${orientation === 'landscape' ? 'width' : 'height'} available for this media; 100 fills it. ${
              (parseScale(scale) ?? 100) > 100 ? 'Above 100 the media is enlarged and its edges are cropped.' : 'Below 100 the window shrinks and is centred.'
            }`}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
          {SCALE_PRESETS.map((preset) => (
            <Chip
              key={preset}
              size="small"
              label={`${preset}%`}
              clickable={canManage}
              disabled={!canManage}
              color={parseScale(scale) === preset ? 'primary' : 'default'}
              variant={parseScale(scale) === preset ? 'filled' : 'outlined'}
              onClick={() => setScale(String(preset))}
            />
          ))}
        </Stack>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} disabled={!canManage} inputProps={{ maxLength: 200 }} />
        <TextField label="Eyebrow" value={eyebrow} onChange={(e) => setEyebrow(e.target.value)} disabled={!canManage} inputProps={{ maxLength: 200 }} />
        <TextField label="Subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} disabled={!canManage} inputProps={{ maxLength: 200 }} />
        <TextField label="Tag (pill)" value={tag} onChange={(e) => setTag(e.target.value)} disabled={!canManage} inputProps={{ maxLength: 200 }} />
      </Box>

      {mediaType === 'image' ? (
        <TextField label="Show for (seconds)" type="number" value={duration} onChange={(e) => setDuration(e.target.value)} error={!!errors.duration} helperText={errors.duration} disabled={!canManage} inputProps={{ min: 1, max: 120 }} />
      ) : (
        <TextField
          label="Play how many times (loops)"
          type="number"
          value={loops}
          onChange={(e) => setLoops(e.target.value)}
          error={!!errors.loops}
          helperText={errors.loops || 'After this many loops the kiosk moves on. If it is the only visible item it keeps looping.'}
          disabled={!canManage}
          inputProps={{ min: 1, max: 20 }}
        />
      )}

      <FormControlLabel control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} disabled={!canManage} />} label="Visible on the kiosk" />

      {canManage && (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {isEdit && (
            <Button color="error" onClick={() => setConfirmDelete(true)} sx={{ mr: 'auto' }}>
              Delete
            </Button>
          )}
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || uploading}>
            {saving ? 'Saving...' : isEdit ? 'Save' : 'Add media'}
          </Button>
        </Stack>
      )}

      <ConfirmDialog open={confirmDelete} title="Delete media" message={`Delete "${item?.title || 'this item'}" from the playlist? This cannot be undone.`} onConfirm={handleDelete} onClose={() => setConfirmDelete(false)} />
    </Stack>
  );
};

export default ItemEditor;
